import React from 'react';

import {AmountInput, Button, Input, Select, SpinningLoader} from '@/components';
import Modal from '@/components/Modals';
import type {AdminBusinessInvoice, AfriexAdminAmount} from '@/types';
import {BusinessInvoiceStatus} from '@/types';
import {capitalizeWords, formatValues} from '@/utils/dashboard';
import {logRejection} from '@/utils/logRejection';

import type {InvoiceStatusDraft} from './types';
import {InvoiceStatusActionType} from './types';
import useInvoiceStatusUpdate from './useInvoiceStatusUpdate';

type InvoiceStatusModalProps = {
  isOpen: boolean;
  onClose: () => void;
  invoice: AdminBusinessInvoice;
  /** Called after a successful update so the list refreshes in place. */
  onSuccess?: () => void | Promise<void>;
  /** Test hook prefix; each rendered element namespaces under it. */
  formDataCy?: string;
};

// Which transitions are legal is the server's to define; until it does, every
// status is offered.
const STATUS_OPTIONS = Object.values(BusinessInvoiceStatus).map((status) => ({
  key: capitalizeWords(status.replace(/_/g, ' ')),
  value: status,
}));

const InvoiceStatusModal = ({
  isOpen,
  onClose,
  invoice,
  onSuccess,
  formDataCy,
}: InvoiceStatusModalProps) => {
  const {draft, dispatch, handleSubmit, isLoading} = useInvoiceStatusUpdate(
    invoice,
    onClose,
    onSuccess,
  );

  const update = (payload: Partial<InvoiceStatusDraft>) =>
    dispatch({type: InvoiceStatusActionType.UPDATE_INVOICE_STATUS, payload});

  const isPartPayment = draft.status === BusinessInvoiceStatus.PartiallyPaid;
  const alreadyPaid = invoice.amount.paid ?? 0;
  const outstanding = invoice.amount.due ?? invoice.amount.value - alreadyPaid;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      width="w-11/12 sm:w-5/12 md:w-5/12 lg:w-5/12 xl:w-3/12 px-4 sm:px-0"
      closeOnOutsideClick
      title={`Update Invoice ${invoice.meta.reference}`}
      dataCy={formDataCy}
    >
      <div className="pt-3 px-2">
        {isLoading ? (
          <div className="flex w-full h-full justify-center items-center my-5">
            <SpinningLoader />
          </div>
        ) : (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleSubmit(draft).catch(
                logRejection('Failed to update invoice status'),
              );
            }}
            className="flex flex-col gap-1"
          >
            <div
              className="mb-3 rounded-lg border border-gray-200 dark:border-dark-borderHeavy px-3 py-2"
              data-cy={formDataCy ? `${formDataCy}-details` : undefined}
            >
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {invoice.meta.reference}
              </p>
              <p className="text-sm text-gray-800 dark:text-gray-100">
                {formatValues(invoice.amount.value, 'money')}{' '}
                {invoice.amount.currencyCode} invoiced,{' '}
                {formatValues(alreadyPaid, 'money')} paid,{' '}
                {formatValues(outstanding, 'money')} outstanding
              </p>
            </div>

            <Select
              label="Status"
              value={draft.status ?? ''}
              dataCy={formDataCy ? `${formDataCy}-status` : undefined}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                update({status: e.target.value as BusinessInvoiceStatus})
              }
              options={STATUS_OPTIONS}
              className="w-full"
            />

            {/* Only a part payment needs an amount: every other status is a
                statement about the invoice, not about money received. */}
            {isPartPayment ? (
              <AmountInput
                label="Amount paid"
                fixedCurrency={invoice.amount.currencyCode}
                min={0}
                dataCy={formDataCy ? `${formDataCy}-amount-paid` : undefined}
                value={
                  {
                    amount: draft.amountPaid,
                    currency: invoice.amount.currencyCode,
                  } as AfriexAdminAmount
                }
                onChange={(value: AfriexAdminAmount) =>
                  update({amountPaid: value.amount})
                }
              />
            ) : null}

            <Input
              label="Note"
              placeholder="Optional"
              type="text"
              dataCy={formDataCy ? `${formDataCy}-note` : undefined}
              value={draft.note}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                update({note: e.target.value})
              }
              className="w-full isFull"
            />

            <div className="mt-4 flex justify-end gap-3">
              <Button
                onClick={onClose}
                colorScheme="cyan"
                variant="ghost"
                dataCy={formDataCy ? `${formDataCy}-cancel` : undefined}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                colorScheme="cyan"
                disabled={isLoading}
                dataCy={formDataCy ? `${formDataCy}-submit` : undefined}
              >
                Update
              </Button>
            </div>
          </form>
        )}
      </div>
    </Modal>
  );
};

export default InvoiceStatusModal;
